<template>
    <div class="map-figure">
        <div ref="mapScrollWrapper" id="full-map-scroll-wrapper" class="map-scroll-wrapper" @touchstart="onTouchStart"
            @touchmove="onTouchMove" @touchend="onTouchEnd">
            <div id="full-map-scroll-wrapper-item" ref="mapScrollItem" class="map-figure map-scroll-wrapper__map"
                :style="{
                    transform: `translateX(${cardSettingsStore.CURRENT_TRANSLATE_X}px)`,
                }">
                <div v-if="!zone">
                    <picture>
                        <source :media="`(max-width: ${MOBILE_TOP}px)`"
                            :srcset="`${getURL()}/${require('@/assets/img/Map/newcliptestmap.jpg')}`" />
                        <img class="map-figure__bg" :src="`${getURL()}/${require('@/assets/img/Map/newcliptestmap.jpg')}`" />
                    </picture>

                    <figure class="map-figure__layer">
                        <svg class="map-figure__svg" width="1920" height="700" viewBox="0 0 1920 700" fill="none"
                            xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                            <Northeast @click="onClick" @mouseover="throttleOver" @mouseout="throttleOut" />
                            <Southwest @click="onClick" @mouseover="throttleOver" @mouseout="throttleOut" />
                            <Southeast @click="onClick" @mouseover="throttleOver" @mouseout="throttleOut" />
                        </svg>

                    </figure>

                </div>
                <!-- Карта конкретного участка -->

                <div v-else class="map-detail">
                    <div  class="map-detail__map">
          
                        <NortheastDetail v-if="zone === 'northeast'" />
                        <SouthwestDetail  v-if="zone === 'southwest'" />
                        <SoutheastDetail v-if="zone === 'southeast'" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { throttle } from 'lodash';
import { ref, computed, onBeforeUnmount, onMounted, watch } from 'vue';
import NortheastDetail from '@/components/map/map-detail/NortheastDetail.vue';
import SouthwestDetail from '@/components/map/map-detail/SouthwestDetail.vue';
import SoutheastDetail from '@/components/map/map-detail/SoutheastDetail.vue';
import Northeast from './map-sections/Northeast.vue';
import Southwest from './map-sections/Southwest.vue';
import Southeast from './map-sections/Southeast.vue';
import { landPlotStore } from '@/store/modules/LandPLot';
import { cardSettingsStore } from '@/store/modules/CardSettings';
import { getURL } from '@/helpers/routeHelpers';
import { MOBILE_TOP } from '@/constants/sizes';

const mapScroll = {
    touchStartPosition: 0,
    touchMovePosition: 0,
    deltaX: 0,
    animationFrameId: 0,
    isScrolling: false,
    startTranslateX: 0,
};

let resizeObserver: ResizeObserver | null = null;

const mapScrollWrapper = ref<HTMLElement | null>(null);
const mapScrollItem = ref<HTMLElement | null>(null);

const zone = computed(() => {
    return landPlotStore.CURRENT_SECTION_ZONE;
});

function createScrollDataObject() {
    let Xmax = 0;

    if (mapScrollWrapper.value && mapScrollItem.value) {
        Xmax =
            mapScrollWrapper.value.getBoundingClientRect().width -
            mapScrollItem.value.getBoundingClientRect().width;

        cardSettingsStore.SET_DATA({
            mapWrapper: mapScrollWrapper.value,
            mapWrapperItem: mapScrollItem.value,
            z: mapScrollWrapper
                ? mapScrollWrapper.value.getBoundingClientRect().width
                : 0,
            vw: mapScrollItem
                ? mapScrollItem.value.getBoundingClientRect().width
                : 0,
            Xmax: Xmax,
        });
    }
}

async function onClick(data: string) {
    cardSettingsStore.SET_STATUS_CARD_PLOT_ZONE_SHOW(false);
    landPlotStore.SET_CURRENT_SECTION_ZONE(data);
}

async function showLandPlotCardZone(data: {
    value: string;
    e: MouseEvent;
}) {
    const { value, e } = data;

    if (e.target instanceof Element) {
        const target = e.currentTarget;
        //@ts-ignore
        const sectionTitle = target.querySelector('.section-title');
        const mapRect = cardSettingsStore.MAP_DOM_RECT;

        if (sectionTitle) {
            sectionTitle.style.opacity = '0';
        }

        const dataRect = e.target.getBoundingClientRect();
        const reselutDataRect = {
            bottom: dataRect.bottom,
            height: dataRect.height,
            left: dataRect.left,
            right: dataRect.right,
            top: dataRect.top,
            topMap: mapRect.top,
            width: dataRect.width,
            x: dataRect.x,
            y: dataRect.y - mapRect.y,
        };

        cardSettingsStore.SET_POSITION_CARD_PLOT_ZONE(reselutDataRect);

        cardSettingsStore.SET_STATUS_CARD_PLOT_ZONE_SHOW(true);
        await landPlotStore.CREATE_LAND_PLOT_CARD_ZONE(value);
    }
}

function mouseOutHandler(e: MouseEvent) {
    if (e.target instanceof Element) {
        const target = e.currentTarget;
        cardSettingsStore.SET_STATUS_CARD_PLOT_ZONE_SHOW(false);
    }
}

function throttleOver(data: { value: string; e: MouseEvent }) {
    if (window.innerWidth >= 1200){
    throttle(showLandPlotCardZone, 1000)(data);
}
}

function throttleOut(e: any) {
    throttle(mouseOutHandler, 1000)(e);
}

function resetFilter() {
    landPlotStore.ZONE_RESET_LAND_PLOT_FILTERS();
    landPlotStore.SET_FILTER_STATUS(false);
}

function lerp(start: number, end: number, interpolation: number) {
    return (1 - interpolation) * start + interpolation * end;
}

function render() {
    let tempDeltaX = lerp(
        mapScroll.deltaX,
        mapScroll.touchMovePosition,
        0.08,
    );
    tempDeltaX = Math.floor(tempDeltaX * 100) / 100;

    if (mapScroll.deltaX === tempDeltaX) {
        if (!mapScroll.isScrolling) {
            cancelAnimationFrame(mapScroll.animationFrameId);
        }
    } else {
        mapScroll.deltaX = tempDeltaX;

        cardSettingsStore.SET_TRANSLATE_X(
            mapScroll.startTranslateX +
            (mapScroll.deltaX - mapScroll.touchStartPosition),
        );

        mapScroll.animationFrameId = requestAnimationFrame(render);
    }
}

function onTouchStart(e: TouchEvent) {
    cancelAnimationFrame(mapScroll.animationFrameId);

    mapScroll.isScrolling = false;
    mapScroll.touchStartPosition = mapScroll.touchMovePosition =
        e.touches[0].clientX;
    mapScroll.startTranslateX = cardSettingsStore.TRANSLATE_X;
}

function onTouchMove(e: TouchEvent) {
    mapScroll.touchMovePosition = e.touches[0].clientX;

    if (!mapScroll.isScrolling) {
        render();

        mapScroll.isScrolling = true;
    }
}

function onTouchEnd() {
    mapScroll.isScrolling = false;
}

function setDefaultPosition() {
    if (!zone.value && cardSettingsStore.MAP_DOM_RECT.width < MOBILE_TOP) {
        cardSettingsStore.SET_TRANSLATE_X(0);
    } else {
        cardSettingsStore.SET_TRANSLATE_X(
            cardSettingsStore.TRANSLATE_CENTER,
        );
    }
}

function onWindowResize() {
    cardSettingsStore.SET_TRANSLATE_X(
        cardSettingsStore.CURRENT_TRANSLATE_X,
    );
}

watch(zone, setDefaultPosition);

onMounted(() => {
    setTimeout(() => {
        createScrollDataObject();

        setDefaultPosition();
    }, 0);

    resizeObserver = new ResizeObserver(createScrollDataObject);

    if (mapScrollWrapper.value && mapScrollItem.value) {
        resizeObserver.observe(mapScrollItem.value);
        resizeObserver.observe(mapScrollWrapper.value);
    }

    window.addEventListener('resize', onWindowResize);
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();

    window.removeEventListener('resize', onWindowResize);
});
</script>

<style lang="less">
.map-scroll-wrapper {

    // .map-scroll-wrapper__map
    &__map {
        @media (min-width: @size-xl-bottom) {
            min-width: auto;
            transform: unset !important;
        }

        @media (max-width: @size-desktop-top) {
            min-width: calc(450 * 1920px / 700);
            transition: all 0.2s ease;
        }

        @media (max-width: @size-tablet-top) {
            min-width: calc(416 * 1920px / 700);
        }

        @media (max-width: @size-mobile-top) {
            min-width: calc(271 * 1920px / 700);
        }
    }
}

.map-figure {
    display: grid;
    grid-template-columns: 100% 100%;
    align-items: center;
    overflow: hidden;
    position: relative;

    // .map-figure__bg
    &__bg {
        display: block;
        width: 100%;
    }

    &__layer {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;

        &--disabled {
            pointer-events: none;
        }
    }

    &__svg {
        display: block;
        width: 100%;
        height: auto;
    }
}

.event {
    path {
        transition: all 0.3s ease;
        cursor: pointer;

        &:hover {
            fill-opacity: 0.6 !important;
        }
    }
}

.map-detail {
    display: grid;
    grid-template-columns: 100% 100%;
    align-items: center;
}

.map-section {
    cursor: pointer;
}

.section-disabled {
    pointer-events: none;
}
</style>
